/**
 * Realtime room state.
 *
 * Owned by RoomClient (T048); UI components are read-only consumers via
 * useStore selectors. This split keeps WebRTC plumbing out of React's
 * render cycle and lets us unit-test RoomClient without rendering anything.
 *
 * Phase 2: skeleton only — slices are empty / default. Phase 3 (T046+)
 * populates them as the join flow does its work.
 */
import { create } from 'zustand'
import type { MediaFailure } from './media.js'
import { readLayoutMode, writeLayoutMode } from '../lib/layoutPreference.js'
import type { LayoutMode } from '../lib/layoutPreference.js'
import type { QualityLevel } from './connectionQuality.js'

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'lost'

export interface PeerMediaState {
  mic: boolean
  cam: boolean
}

export interface PeerInfo {
  peerId: string
  displayName: string
  /** MediaStream containing whatever remote tracks we've consumed so far. */
  stream: MediaStream
  mediaState: PeerMediaState
}

export interface RoomStoreState {
  connectionStatus: ConnectionStatus
  localPeerId: string | null
  /** Authoritative display name returned by the server join response. */
  localDisplayName: string | null
  /** Remote peers only — local peer is rendered separately from `localStream`. */
  peers: Record<string, PeerInfo>
  localStream: MediaStream | null
  localMediaState: PeerMediaState
  /** Non-null when local getUserMedia failed — drives the permission-denied UX. */
  localMediaError: MediaFailure | null
  /** Current layout mode for the local user's view (FR-001). Restored from localStorage. */
  layoutMode: LayoutMode
  /** Id of the participant pinned in spotlight featured slot. null = use default rule. Per-session only. */
  pinnedId: string | null
  /** Currently selected/published camera facing. Drives local tile mirroring. Per-session only. */
  cameraFacing: 'user' | 'environment'
  /** Number of video-input devices reported by the browser. Drives switch-camera control visibility. */
  videoInputCount: number
  /** Per-remote-peer connection quality, keyed by peerId (007). Absent → 'unknown'. */
  peerQuality: Record<string, QualityLevel>
  /** Local participant's outbound connection quality (007). */
  localQuality: QualityLevel
}

// Base initial state without layoutMode — that is read from localStorage at
// store creation time and kept across reset() calls.
const baseInitialState = {
  connectionStatus: 'idle' as ConnectionStatus,
  localPeerId: null as string | null,
  localDisplayName: null as string | null,
  peers: {} as Record<string, PeerInfo>,
  localStream: null as MediaStream | null,
  localMediaState: { mic: false, cam: false } as PeerMediaState,
  localMediaError: null as MediaFailure | null,
  pinnedId: null as string | null,
  cameraFacing: 'user' as 'user' | 'environment',
  videoInputCount: 0,
  peerQuality: {} as Record<string, QualityLevel>,
  localQuality: 'unknown' as QualityLevel,
}

export interface RoomStoreActions {
  /** Replace state entirely — used on join completion and on leave.
   *  Keeps the persisted layoutMode; resets pinnedId to null. */
  reset: () => void
  setConnectionStatus: (status: ConnectionStatus) => void
  setLocalPeerId: (peerId: string | null) => void
  setLocalDisplayName: (name: string | null) => void
  setLocalStream: (stream: MediaStream | null) => void
  setLocalMediaState: (patch: Partial<PeerMediaState>) => void
  setLocalMediaError: (error: MediaFailure | null) => void
  addPeer: (peer: PeerInfo) => void
  removePeer: (peerId: string) => void
  patchPeerMediaState: (peerId: string, patch: Partial<PeerMediaState>) => void
  /** Replace the MediaStream reference for a peer — triggers a re-render. */
  replacePeerStream: (peerId: string, stream: MediaStream) => void
  /** Set the layout mode and persist to localStorage (FR-013). */
  setLayoutMode: (mode: LayoutMode) => void
  /** Set pinnedId (in-memory, not persisted). */
  setPinnedId: (id: string | null) => void
  /** Convenience: set pinnedId AND switch to spotlight (click-to-pin, FR-005). */
  pinParticipant: (id: string) => void
  /** Set the currently selected camera facing (front/back). Per-session only. */
  setCameraFacing: (facing: 'user' | 'environment') => void
  /** Set the number of video-input devices reported by the browser. */
  setVideoInputCount: (n: number) => void
  /** Set a remote peer's connection quality (007). No-op for unknown peers is allowed. */
  setPeerQuality: (peerId: string, level: QualityLevel) => void
  /** Set the local participant's outbound connection quality (007). */
  setLocalQuality: (level: QualityLevel) => void
}

export type RoomStore = RoomStoreState & RoomStoreActions

export const useRoomStore = create<RoomStore>((set) => ({
  ...baseInitialState,
  layoutMode: readLayoutMode(),

  reset: () =>
    set((s) => ({
      ...baseInitialState,
      // Keep the persisted layoutMode across reset (leave/rejoin), clear pinnedId.
      layoutMode: s.layoutMode,
      pinnedId: null,
    })),

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setLocalPeerId: (localPeerId) => set({ localPeerId }),
  setLocalDisplayName: (localDisplayName) => set({ localDisplayName }),
  setLocalStream: (localStream) => set({ localStream }),

  setLocalMediaState: (patch) =>
    set((s) => ({ localMediaState: { ...s.localMediaState, ...patch } })),

  setLocalMediaError: (localMediaError) => set({ localMediaError }),

  addPeer: (peer) => set((s) => ({ peers: { ...s.peers, [peer.peerId]: peer } })),

  removePeer: (peerId) =>
    set((s) => {
      if (!(peerId in s.peers) && !(peerId in s.peerQuality)) return s
      const next = { ...s.peers }
      delete next[peerId]
      // Drop the peer's quality entry too — no stale gauge after they leave (007).
      const nextQuality = { ...s.peerQuality }
      delete nextQuality[peerId]
      return { peers: next, peerQuality: nextQuality }
    }),

  patchPeerMediaState: (peerId, patch) =>
    set((s) => {
      const existing = s.peers[peerId]
      if (!existing) return s
      return {
        peers: {
          ...s.peers,
          [peerId]: { ...existing, mediaState: { ...existing.mediaState, ...patch } },
        },
      }
    }),

  replacePeerStream: (peerId, stream) =>
    set((s) => {
      const existing = s.peers[peerId]
      if (!existing) return s
      return {
        peers: {
          ...s.peers,
          [peerId]: { ...existing, stream },
        },
      }
    }),

  setLayoutMode: (mode) => {
    writeLayoutMode(mode)
    set({ layoutMode: mode })
  },

  setPinnedId: (id) => set({ pinnedId: id }),

  pinParticipant: (id) => {
    writeLayoutMode('spotlight')
    set({ pinnedId: id, layoutMode: 'spotlight' })
  },

  setCameraFacing: (cameraFacing) => set({ cameraFacing }),

  setVideoInputCount: (videoInputCount) => set({ videoInputCount }),

  setPeerQuality: (peerId, level) =>
    set((s) => {
      if (s.peerQuality[peerId] === level) return s
      return { peerQuality: { ...s.peerQuality, [peerId]: level } }
    }),

  setLocalQuality: (level) =>
    set((s) => (s.localQuality === level ? s : { localQuality: level })),
}))

// DEV-only test seam: expose the store on window so Playwright visual specs
// can seed state deterministically without spinning up real WebRTC plumbing.
// Vite strips this entire block in production builds (import.meta.env.DEV is
// statically false at build time → dead-code eliminated). No prod impact.
if (import.meta.env.DEV) {
  ;(window as unknown as Record<string, unknown>)['__roomStore'] = useRoomStore
}
